import React, { useState, useEffect, useRef } from 'react';
import { taskDataService } from '../../../services/taskDataService';
import NotificationService from '../../../services/NotificationService';
import {
  Box,
  Typography,
  Paper,
  styled,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { storage } from '../../../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { format, parseISO, isAfter } from 'date-fns';
import { tr } from 'date-fns/locale';

// İkonlar
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ImageIcon from '@mui/icons-material/Image';
import CancelIcon from '@mui/icons-material/Cancel';

// Özel bileşenler
import TaskDetailModal from './TaskDetailModal';

// Stil tanımlamaları
const TaskCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  }
}));

const TaskCardContent = styled(CardContent)({
  flexGrow: 1,
});

const StatusChip = styled(Chip)<{ customcolor?: string }>(({ theme, customcolor }) => ({
  fontWeight: 'bold',
  backgroundColor: customcolor ? `${customcolor}20` : undefined,
  color: customcolor,
  borderColor: customcolor ? `${customcolor}50` : undefined,
  '& .MuiChip-icon': {
    color: 'inherit'
  }
}));

const ViewToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(2),
}));

interface Task {
  id: string;
  name: string;
  description: string;
  status: string;
  completionType: string;
  createdAt: number;
  deadline?: string;
  isRecurring: boolean;
  imageUrls?: string[];
  acceptedAt?: number;
  startedAt?: number;
  completedAt?: number;
  personnelId: string;
  personnelName: string;
  groupId?: string;
  groupName: string;
  branchesId?: string | null;
  branchName: string;
  companyId?: string;
  requestCompletionPhoto?: boolean;
  completionPhotoUrl?: string;
  canBeRejected?: boolean;
  rejectReason?: string;
  rejectedAt?: number;
  rejectedBy?: string;
  renewedAt?: number;
  updatedBy?: string;
  createdByUid?: string;
}

interface NormalTasksComponentProps {
  companyId?: string; // Dışarıdan (PersonnelTaskBoard) geçirilecek şirket ID'si
  userFullName?: string; // Dışarıdan (PersonnelTaskBoard) geçirilecek kullanıcı tam adı
}

const NormalTasksComponent: React.FC<NormalTasksComponentProps> = ({ companyId: propCompanyId, userFullName }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // ✅ userDetails AuthContext cache'inden - Firebase sorgusu YOK!
  const { currentUser, userDetails } = useAuth();
  
  // Gecikme açıklaması modal'ı için state'ler
  const [isDelayReasonModalOpen, setIsDelayReasonModalOpen] = useState(false);
  const [delayReasonText, setDelayReasonText] = useState('');
  const [isDelayReasonValid, setIsDelayReasonValid] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<Task | null>(null);
  // Bildirim için state
  const [notification, setNotification] = useState<{message: string, isError: boolean} | null>(null);

  // Görev detay modalı için state'ler
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Yeni eklenen state'ler
  const [selectedFileForCompletion, setSelectedFileForCompletion] = useState<File | null>(null);
  const [isCompletingWithPhoto, setIsCompletingWithPhoto] = useState(false);
  const [taskToCompleteWithPhoto, setTaskToCompleteWithPhoto] = useState<Task | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Görev reddetmek için yeni state ekle
  const [isRejectReasonModalOpen, setIsRejectReasonModalOpen] = useState(false);
  const [rejectReasonText, setRejectReasonText] = useState('');
  const [isRejectReasonValid, setIsRejectReasonValid] = useState(false);
  const [taskToReject, setTaskToReject] = useState<Task | null>(null);

  // Bildirim gösterme fonksiyonu
  const showNotification = (message: string, isError: boolean = false) => {
    setNotification({ message, isError });
    // 3 saniye sonra bildirimi kaldır
    setTimeout(() => {
      setNotification(null);
    }, isError ? 4000 : 2000);
  };

  // LocalStorage'dan kullanıcı görünüm tercihini yükle
  useEffect(() => {
    const savedViewMode = localStorage.getItem('personnelTaskViewMode');
    if (savedViewMode && (savedViewMode === 'card' || savedViewMode === 'list')) {
      setViewMode(savedViewMode as 'card' | 'list');
    }
  }, []);

  // Görünüm modu değişikliği
  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: 'card' | 'list' | null
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      // LocalStorage'a kullanıcı tercihini kaydet
      localStorage.setItem('personnelTaskViewMode', newViewMode);
    }
  };

  // Sayfa değişikliği
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişikliği
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Görev durumuna göre renk döndürme
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return '#FFA000'; // Turuncu
      case 'accepted':
        return '#1976D2'; // Mavi
      case 'started':
        return '#43A047'; // Yeşil
      case 'completed':
        return '#6A1B9A'; // Mor
      default:
        return '#757575'; // Gri
    }
  };

  // Görev durumuna göre ikon döndürme
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <PendingIcon />;
      case 'accepted':
        return <TaskAltIcon />;
      case 'started':
        return <PlayCircleFilledIcon />;
      case 'completed':
        return <CheckCircleIcon />;
      default:
        return <ErrorOutlineIcon />;
    }
  };

  // Görev durumuna göre etiket döndürme
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'accepted':
        return 'Kabul Edildi';
      case 'started':
        return 'Başlatıldı';
      case 'completed':
        return 'Tamamlandı';
      case 'rejected':
        return 'Reddedildi';
      default:
        return 'Bilinmiyor';
    }
  };

  // Faz 5 DILIM 6: Personelin normal (tekrarsız) görevlerini SQL'den yükle (taskDataService)
  useEffect(() => {
    const loadTasks = async () => {
      if (!currentUser) {
        setError('Kullanıcı oturumu bulunamadı');
        setLoading(false);
        return;
      }

      try {
        const companyId = propCompanyId || userDetails?.companyId;
        if (!companyId) {
          setError('Kullanıcı bir şirkete ait değil');
          setLoading(false);
          return;
        }

        try {
          localStorage.setItem('userCompanyId', companyId);
          localStorage.setItem('currentCompanyId', companyId);
        } catch (e) {
          console.error('localStorage erişimi sırasında hata:', e);
        }

        const parseImgs = (raw?: string | null): string[] => {
          if (!raw) return [];
          try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
        };
        const toMs = (iso?: string | null): number => (iso ? new Date(iso).getTime() : Date.now());
        const toMsOpt = (iso?: string | null): number | undefined => (iso ? new Date(iso).getTime() : undefined);

        // Kullanıcıya atanmış 'single' (tekrarsız) görevler — sayfalı çek
        const all: any[] = [];
        let page = 1;
        for (;;) {
          const res = await taskDataService.listTasks({
            taskType: 'single',
            personnelId: currentUser.uid,
            page,
            pageSize: 200,
          });
          all.push(...res.items);
          if (res.items.length === 0 || all.length >= (res.totalCount || 0)) break;
          page++;
        }

        const tasksList: Task[] = all
          .filter((tk) => !tk.isRecurring)
          .map((tk) => ({
            id: String(tk.id),
            name: tk.name || 'İsimsiz Görev',
            description: tk.description || '',
            status: tk.status || 'pending',
            completionType: tk.completionType || 'button',
            createdAt: toMs(tk.createdAt),
            deadline: tk.deadline || undefined,
            isRecurring: tk.isRecurring || false,
            imageUrls: parseImgs(tk.imageUrls),
            acceptedAt: toMsOpt(tk.acceptedAt),
            startedAt: toMsOpt(tk.startedAt),
            completedAt: toMsOpt(tk.completedAt),
            personnelId: tk.personnelFirebaseUid || '',
            personnelName: currentUser.displayName || userFullName || '',
            groupId: tk.groupId != null ? String(tk.groupId) : undefined,
            groupName: '',
            branchesId: tk.branchesId || null,
            branchName: '',
            companyId: companyId,
            requestCompletionPhoto: tk.requestCompletionPhoto || false,
            completionPhotoUrl: tk.completionPhotoUrl || undefined,
            canBeRejected: tk.canBeRejected || false,
            rejectReason: tk.rejectReason || undefined,
            rejectedAt: toMsOpt(tk.rejectedAt),
            rejectedBy: tk.rejectedByUid || undefined,
            renewedAt: toMsOpt(tk.renewedAt),
            updatedBy: undefined,
            createdByUid: tk.createdByUid || undefined,
          } as Task));

        tasksList.sort((a, b) => b.createdAt - a.createdAt);
        setTasks(tasksList);
      } catch (error) {
        console.error('Görevler yüklenirken hata:', error);
        setError('Görevler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    loadTasks();
  }, [currentUser, propCompanyId, userFullName, userDetails]);

  // Görev kabul etme işlemi — SQL (changeTaskStatus)
  const handleAcceptTask = async (taskId: string) => {
    if (!currentUser) return;

    try {
      await taskDataService.changeTaskStatus(Number(taskId), {
        action: 'accept',
        byUid: currentUser.uid,
      });

      showNotification('Görev başarıyla kabul edildi', false);
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId ? { ...task, status: 'accepted' } : task
        )
      );
    } catch (error) {
      console.error('Görev kabul edilirken hata:', error);
      showNotification('Görev kabul edilirken bir hata oluştu', true);
    }
  };

  // Görevi başlatma işlemi — SQL (changeTaskStatus)
  const handleStartTask = async (taskId: string) => {
    if (!currentUser) return;

    try {
      await taskDataService.changeTaskStatus(Number(taskId), {
        action: 'start',
        byUid: currentUser.uid,
      });

      showNotification('Görev başarıyla başlatıldı', false);
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId ? { ...task, status: 'started' } : task
        )
      );
    } catch (error) {
      console.error('Görev başlatılırken hata:', error);
      showNotification('Görev başlatılırken bir hata oluştu', true);
    }
  };

  // Dosya seçildiğinde tetiklenir
  const handleFileSelectForCompletion = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFileForCompletion(file);
      // Otomatik olarak yükleme ve tamamlama işlemini başlat
      if (taskToCompleteWithPhoto) {
        await uploadCompletionPhotoAndCompleteTask(taskToCompleteWithPhoto, file);
      }
    }
    // Input'u sıfırla ki aynı dosya tekrar seçilebilsin
    if (event.target) {
      event.target.value = '';
    }
  };

  // Resmi yükle ve görevi tamamla
  const uploadCompletionPhotoAndCompleteTask = async (task: Task, file: File) => {
    if (!currentUser || !propCompanyId) {
      showNotification('Kullanıcı veya şirket bilgisi eksik.', true);
      return;
    }
    setIsCompletingWithPhoto(true);
    try {
      const timestamp = new Date().getTime();
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `${task.id}_${timestamp}.${fileExtension}`;
      const photoRef = storageRef(storage, `companies/${propCompanyId}/completion_photos/${task.id}/${uniqueFileName}`);

      await uploadBytes(photoRef, file);
      const photoURL = await getDownloadURL(photoRef);

      // Görevi SQL'de tamamla (changeTaskStatus) — foto URL ile
      const wasDelayed = !!(task.deadline && isAfter(new Date(), parseISO(task.deadline)));
      await taskDataService.changeTaskStatus(Number(task.id), {
        action: 'complete',
        byUid: currentUser.uid,
        completionPhotoUrl: photoURL,
        wasDelayed,
      });
      showNotification('Görev başarıyla tamamlandı ve fotoğraf yüklendi.', false);
      setTasks(prevTasks =>
        prevTasks.map(t =>
          t.id === task.id ? { ...t, status: 'completed', completionPhotoUrl: photoURL } : t
        )
      );
    } catch (error) {
      console.error('Fotoğraf yükleme veya görev tamamlama hatası:', error);
      showNotification('Fotoğraf yüklenirken veya görev tamamlanırken bir hata oluştu.', true);
    } finally {
      setIsCompletingWithPhoto(false);
      setSelectedFileForCompletion(null);
      setTaskToCompleteWithPhoto(null);
    }
  };

  // Görevi tamamlama işlemi (ana yönlendirici)
  const handleCompleteTask = async (task: Task) => {
    if (!currentUser) return;

    // Eğer görev fotoğraf gerektiriyorsa ve henüz fotoğraf seçilmemişse
    if (task.requestCompletionPhoto) {
      setTaskToCompleteWithPhoto(task); // Hangi görev için fotoğraf beklendiğini set et
      fileInputRef.current?.click(); // Dosya seçme penceresini aç
      // Resim seçildiğinde handleFileSelectForCompletion tetiklenecek ve yükleme başlayacak.
      return; 
    }

    // Fotoğraf gerektirmeyen veya zaten seçilmiş (bu akışta olmayacak ama genel)
    // normal tamamlama veya gecikme açıklaması isteme mantığı
    try {
      if (task.deadline) {
        const deadlineDate = parseISO(task.deadline);
        const now = new Date();
        if (isAfter(now, deadlineDate)) {
          setTaskToComplete(task); // Bu, gecikme açıklaması modalı için
          setIsDelayReasonModalOpen(true);
          return;
        }
      }
      await completeTaskDirectly(task.id);
    } catch (error) {
      console.error('Görev tamamlanırken hata:', error);
      showNotification('Görev tamamlanırken hata oluştu.', true);
    }
  };

  // Görevi tamamla — SQL (changeTaskStatus)
  const completeTaskDirectly = async (taskId: string, delayReason?: string) => {
    if (!currentUser) return;

    try {
      await taskDataService.changeTaskStatus(Number(taskId), {
        action: 'complete',
        byUid: currentUser.uid,
        wasDelayed: delayReason ? true : undefined,
        delayReason: delayReason || undefined,
      });

      showNotification('Görev başarıyla tamamlandı', false);
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId ? { ...task, status: 'completed' } : task
        )
      );
    } catch (error) {
      console.error('Görev tamamlanırken hata:', error);
      showNotification('Görev tamamlanırken bir hata oluştu', true);
    }
  };

  // Gecikme açıklaması ile görevi tamamla
  const handleCompleteWithDelayReason = async () => {
    if (!taskToComplete || !delayReasonText.trim()) return;

    try {
      await completeTaskDirectly(taskToComplete.id, delayReasonText.trim());
      // Modal'ı kapat ve state'leri sıfırla
      handleCloseDelayReasonModal();
    } catch (error) {
      console.error('Gecikme açıklaması ile görev tamamlama hatası:', error);
    }
  };

  // Gecikme açıklaması modal'ını kapat
  const handleCloseDelayReasonModal = () => {
    setIsDelayReasonModalOpen(false);
    setDelayReasonText('');
    setIsDelayReasonValid(false);
    setTaskToComplete(null);
  };

  // Görev reddetme modal'ını kapat
  const handleCloseRejectReasonModal = () => {
    setIsRejectReasonModalOpen(false);
    setRejectReasonText('');
    setIsRejectReasonValid(false);
    setTaskToReject(null);
  };

  // Görev reddetme işlemini başlat
  const handleRejectTask = (task: Task) => {
    setTaskToReject(task);
    setIsRejectReasonModalOpen(true);
  };

  // Açıklama ile görevi reddet — SQL (changeTaskStatus)
  const handleRejectWithReason = async () => {
    if (!taskToReject || !rejectReasonText.trim() || !currentUser) return;

    try {
      const createdBy = taskToReject.createdByUid;
      const taskName = taskToReject.name || 'İsimsiz Görev';

      await taskDataService.changeTaskStatus(Number(taskToReject.id), {
        action: 'reject',
        byUid: currentUser.uid,
        rejectReason: rejectReasonText.trim(),
      });
      
      showNotification('Görev başarıyla reddedildi', false);
      
      // Görevi oluşturan kişiye bildirim gönder — .NET notification API (tek çağrı: kaydet + FCM gönder)
      if (createdBy) {
        try {
          console.log('Görev reddi bildirimi gönderiliyor:', createdBy);
          
          const notifCompanyId = propCompanyId || userDetails?.companyId || '';
          await NotificationService.sendNotification(
            notifCompanyId,
            [createdBy],
            'Görev Reddedildi',
            `${userFullName || 'Personel'} tarafından "${taskName}" görevi reddedildi. Sebep: ${rejectReasonText.length > 30 ? rejectReasonText.substring(0, 30) + '...' : rejectReasonText}`
          );
          
          console.log('Bildirim başarıyla gönderildi');
        } catch (notificationError) {
          console.error('Bildirim gönderilirken hata:', notificationError);
          // Bildirim hatası görev reddetme işlemini engellemesin
        }
      } else {
        console.log('Görevi oluşturan kullanıcı bilgisi bulunamadı');
      }
      
      // Görevi hemen güncelleyelim (Firebase dinleyicisi güncellemeden önce)
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskToReject.id 
            ? { ...task, status: 'rejected' } 
            : task
        )
      );
      
      // Modal'ı kapat
      handleCloseRejectReasonModal();
    } catch (error) {
      console.error('Görev reddedilirken hata:', error);
      showNotification('Görev reddedilirken bir hata oluştu', true);
    }
  };

  // Tarih formatı
  const formatDate = (dateString: string | number): string => {
    const date = typeof dateString === 'number' 
      ? new Date(dateString) 
      : parseISO(dateString);
      
    // Kompakt format: GG.AA.YYYY SS:DD
    return format(date, 'dd.MM.yyyy HH:mm', { locale: tr });
  };

  // Kart görünümü
  const renderCardView = () => {
    return (
      <Grid container spacing={2}>
        {tasks.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">
                Atanmış görev bulunmuyor.
              </Typography>
            </Paper>
          </Grid>
        ) : (
          tasks
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task.id}>
                <TaskCard 
                  onClick={() => handleShowTaskDetail(task)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TaskCardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="h6" component="div" fontWeight="bold" gutterBottom>
                        {task.name}
                      </Typography>
                      <StatusChip
                        icon={getStatusIcon(task.status)}
                        label={getStatusLabel(task.status)}
                        customcolor={getStatusColor(task.status)}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                      {task.description || 'Açıklama bulunmuyor.'}
                    </Typography>
                    <Box display="flex" alignItems="center" mb={1}>
                      <AccessTimeIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(task.createdAt)}
                      </Typography>
                    </Box>
                    {task.deadline && (
                      <Box display="flex" alignItems="center">
                        <ErrorOutlineIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                        <Typography variant="body2" color="error">
                          Son Tarih: {formatDate(task.deadline)}
                        </Typography>
                      </Box>
                    )}
                  </TaskCardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                    {renderTaskActions(task)}
                  </CardActions>
                </TaskCard>
              </Grid>
            ))
        )}
      </Grid>
    );
  };

  // Tablo görünümü
  const renderTableView = () => {
    return (
      <TableContainer component={Paper} sx={{ 
        mt: 2, 
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: 2,
        overflow: 'auto',
        maxHeight: '60vh',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Görev Adı</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Açıklama</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Durum</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Oluşturulma Tarihi</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Son Tarih</TableCell>
              <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Atanmış görev bulunmuyor.
                </TableCell>
              </TableRow>
            ) : (
              tasks
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((task) => (
                  <TableRow 
                    key={task.id}
                    hover
                    onClick={() => handleShowTaskDetail(task)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{task.name}</TableCell>
                    <TableCell>
                      {task.description.length > 50
                        ? `${task.description.substring(0, 50)}...`
                        : task.description || 'Açıklama bulunmuyor.'}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        icon={getStatusIcon(task.status)}
                        label={getStatusLabel(task.status)}
                        customcolor={getStatusColor(task.status)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(task.createdAt)}</TableCell>
                    <TableCell>{task.deadline ? formatDate(task.deadline) : '-'}</TableCell>
                    <TableCell align="right">
                      {renderTaskActions(task)}
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Kart görünümü için task action butonlarını render et
  const renderTaskActions = (task: Task) => {
    return (
      <>
        {/* Resim görüntüleme butonu - eğer görevde resim varsa göster */}
        {task.imageUrls && task.imageUrls.length > 0 && (
          <Tooltip title={`${task.imageUrls.length} resim görüntüle (görev detayında)`}>
            <IconButton 
              size="small" 
              color="info"
              onClick={(e) => {
                e.stopPropagation(); // Modal açılmasını engelle
                handleShowTaskDetail(task);
              }}
              sx={{ mr: 1 }}
            >
              <ImageIcon />
            </IconButton>
          </Tooltip>
        )}
      
        {/* Mevcut butonlar */}
        {task.status === 'pending' && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <Button 
              size="small" 
              variant="contained" 
              color="primary"
              onClick={(e) => {
                e.stopPropagation(); // Modal açılmasını engelle
                handleAcceptTask(task.id);
              }}
              sx={{ 
                minWidth: '80px', 
                whiteSpace: 'nowrap',
              }}
            >
              {task.renewedAt ? 'Yeniden Kabul Et' : 'Kabul Et'}
            </Button>
            
            {/* Reddet butonu - Sadece canBeRejected true ise göster */}
            {task.canBeRejected && (
              <Button 
                size="small" 
                variant="outlined" 
                color="error"
                startIcon={<CancelIcon />}
                onClick={(e) => {
                  e.stopPropagation(); // Modal açılmasını engelle
                  handleRejectTask(task);
                }}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Reddet
              </Button>
            )}
          </Box>
        )}
        
        {task.status === 'accepted' && task.completionType === 'qr' && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<QrCodeIcon />}
            disabled
            onClick={(e) => e.stopPropagation()} 
            sx={{ whiteSpace: 'nowrap' }}
          >
            QR Tara
          </Button>
        )}
        
        {task.status === 'accepted' && task.completionType !== 'qr' && (
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={(e) => {
              e.stopPropagation();
              handleStartTask(task.id);
            }}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Başlat
          </Button>
        )}
        
        {task.status === 'started' && task.completionType === 'qr' && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<QrCodeIcon />}
            disabled
            onClick={(e) => e.stopPropagation()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            QR Tara
          </Button>
        )}
        
        {task.status === 'started' && task.completionType !== 'qr' && (
          <Button
            size="small"
            variant="contained"
            style={{ backgroundColor: '#6A1B9A', color: 'white' }}
            onClick={(e) => {
              e.stopPropagation();
              handleCompleteTask(task);
            }}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Tamamla
          </Button>
        )}
        
        {task.status === 'completed' && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            disabled
            onClick={(e) => e.stopPropagation()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Tamamlandı
          </Button>
        )}
        
        {task.status === 'rejected' && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled
            onClick={(e) => e.stopPropagation()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Reddedildi
          </Button>
        )}
      </>
    );
  };

  // Görev detaylarını göster
  const handleShowTaskDetail = (task: Task) => {
    // Görev detaylarını göster
    setSelectedTask(task);
    setIsTaskDetailOpen(true);
    
    // Task'ın branchesId'sinden şirket ID'sini çıkar ve localStorage'a kaydet
    if (task.branchesId) {
      try {
        const branchIdParts = String(task.branchesId).split('/');
        if (branchIdParts.length > 0) {
          const extractedCompanyId = branchIdParts[0];
          localStorage.setItem('currentCompanyId', extractedCompanyId);
          console.log('Task branchesId\'den şirket ID çıkarıldı ve kaydedildi:', extractedCompanyId);
        }
      } catch (e) {
        console.error('branchesId işlenirken hata:', e);
      }
    }
    
    // Kullanıcı adını localStorage'a kaydet
    if (userFullName) {
      localStorage.setItem('userFullName', userFullName);
      console.log('Kullanıcı adı localStorage\'a kaydedildi:', userFullName);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <ViewToggleContainer>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="görünüm modu"
          size="small"
        >
          <ToggleButton value="card" aria-label="kart görünümü">
            <Tooltip title="Kart Görünümü">
              <ViewModuleIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="list" aria-label="liste görünümü">
            <Tooltip title="Liste Görünümü">
              <ViewListIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </ViewToggleContainer>

      {viewMode === 'card' ? renderCardView() : renderTableView()}

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={tasks.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Sayfa başına satır:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />

      {/* Bildirim Snackbar'ı Login.tsx'teki gibi */}
      {notification && (
        <Snackbar 
          open={true}
          autoHideDuration={notification.isError ? 4000 : 2000} 
          onClose={() => setNotification(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setNotification(null)} 
            severity={notification.isError ? "error" : "success"}
            sx={{
              fontWeight: 'bold',
              fontSize: '16px',
              width: '100%'
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}

      {/* Gecikme Açıklaması Modal'ı */}
      <Dialog 
        open={isDelayReasonModalOpen} 
        onClose={handleCloseDelayReasonModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          color: 'error.main',
          fontWeight: 'bold'
        }}>
          <WarningAmberIcon sx={{ mr: 1 }} />
          Gecikme Açıklaması
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            Görevi zamanında tamamlayamadınız. Lütfen gecikme nedeninizi açıklayınız:
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={5}
            fullWidth
            variant="outlined"
            label="Gecikme Nedeni"
            placeholder="Gecikme nedeninizi yazınız..."
            value={delayReasonText}
            onChange={(e) => {
              setDelayReasonText(e.target.value);
              setIsDelayReasonValid(e.target.value.trim().length > 0);
            }}
            inputProps={{
              maxLength: 200
            }}
            helperText={`${delayReasonText.length}/200`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelayReasonModal} color="inherit">
            İptal
          </Button>
          <Button 
            onClick={handleCompleteWithDelayReason} 
            variant="contained" 
            color="primary"
            disabled={!isDelayReasonValid}
          >
            Tamamla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Görev Reddetme Modal'ı */}
      <Dialog 
        open={isRejectReasonModalOpen} 
        onClose={handleCloseRejectReasonModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          color: 'error.main',
          fontWeight: 'bold'
        }}>
          <CancelIcon sx={{ mr: 1 }} />
          Reddetme Nedeni
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            Bu görevi reddetme nedeninizi açıklayınız:
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={5}
            fullWidth
            variant="outlined"
            label="Reddetme Nedeni"
            placeholder="Reddetme nedeninizi yazınız..."
            value={rejectReasonText}
            onChange={(e) => {
              setRejectReasonText(e.target.value);
              setIsRejectReasonValid(e.target.value.trim().length > 0);
            }}
            inputProps={{
              maxLength: 200
            }}
            helperText={`${rejectReasonText.length}/200`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectReasonModal} color="inherit">
            İptal
          </Button>
          <Button 
            onClick={handleRejectWithReason} 
            variant="contained" 
            color="error"
            disabled={!isRejectReasonValid}
          >
            Reddet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Görev Detay Modalı */}
      <TaskDetailModal
        open={isTaskDetailOpen}
        onClose={() => setIsTaskDetailOpen(false)}
        task={selectedTask}
        companyId={propCompanyId}
        currentUserName={userFullName}
      />

      {/* JSX içinde gizli file input */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelectForCompletion}
      />

      {isCompletingWithPhoto && taskToCompleteWithPhoto?.id && (
        <Dialog open={isCompletingWithPhoto}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p:3 }}>
            <CircularProgress sx={{mb: 2}} />
            <Typography>Görev tamamlanıyor ve fotoğraf yükleniyor...</Typography>
            <Typography variant="caption">Lütfen bekleyin.</Typography>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default NormalTasksComponent; 